import React, { useState, useEffect, useRef } from "react";
import { getData, postData } from "../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARDS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, subValue, color, isDark }) => {
  const colors = {
    orders: { bg: isDark ? "rgba(59,130,246,0.12)" : "rgba(59,130,246,0.08)", icon: "#3b82f6" },
    revenue: { bg: isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)", icon: "#22c55e" },
    business: { bg: isDark ? "rgba(139,92,246,0.12)" : "rgba(139,92,246,0.08)", icon: "#8b5cf6" },
    platform: { bg: isDark ? "rgba(236,72,153,0.12)" : "rgba(236,72,153,0.08)", icon: "#ec4899" },
    driver: { bg: isDark ? "rgba(251,191,36,0.12)" : "rgba(251,191,36,0.08)", icon: "#f59e0b" },
  };

  const config = colors[color] || colors.orders;

  return (
    <div style={{
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "16px 20px",
      flex: 1,
      minWidth: "120px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <div style={{
          width: "36px",
          height: "36px",
          borderRadius: "8px",
          background: config.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: config.icon,
        }}>
          {icon}
        </div>
        <span style={{
          fontSize: "13px",
          color: isDark ? "#94a3b8" : "#6b7280",
          fontWeight: 500,
        }}>{label}</span>
      </div>
      <div style={{
        fontSize: "22px",
        fontWeight: 700,
        color: isDark ? "#f1f5f9" : "#111827",
        marginBottom: "4px",
      }}>{value}</div>
      {subValue && (
        <div style={{
          fontSize: "12px",
          color: isDark ? "#64748b" : "#9ca3af",
        }}>{subValue}</div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, isDark }) => {
  const configs = {
    placed: { bg: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)", color: "#22c55e", label: "Placed" },
    confirmed: { bg: isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)", color: "#3b82f6", label: "Confirmed" },
    preparing: { bg: isDark ? "rgba(251,191,36,0.15)" : "rgba(251,191,36,0.1)", color: "#f59e0b", label: "Preparing" },
    ready: { bg: isDark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)", color: "#8b5cf6", label: "Ready" },
    delivered: { bg: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)", color: "#22c55e", label: "Delivered" },
    cancelled: { bg: isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)", color: "#ef4444", label: "Cancelled" },
    completed: { bg: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)", color: "#22c55e", label: "Completed" },
  };

  const config = configs[status?.toLowerCase()] || configs.placed;

  return (
    <span style={{
      display: "inline-block",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 600,
      background: config.bg,
      color: config.color,
    }}>
      {config.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT BADGE
// ─────────────────────────────────────────────────────────────────────────────
const PaymentBadge = ({ method, isDark }) => {
  const isCOD = method?.toLowerCase() === "cod";
  
  return (
    <span style={{
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: "6px",
      fontSize: "11px",
      fontWeight: 500,
      background: isCOD 
        ? (isDark ? "rgba(251,191,36,0.15)" : "rgba(251,191,36,0.1)")
        : (isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"),
      color: isCOD ? "#f59e0b" : "#22c55e",
    }}>
      {isCOD ? "COD" : method || "—"}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN REPORT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const Report = ({ isDark = true }) => {
  // ───────────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const dropdownRef = useRef(null);

  // ───────────────────────────────────────────────────────────────────────────
  // CLOSE DROPDOWN ON CLICK OUTSIDE
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // STATS CALCULATION
  // ───────────────────────────────────────────────────────────────────────────
  const calculateStats = (data) => {
    const totalOrders = data.length;
    const totalRevenue = data.reduce((sum, order) => sum + parseFloat(order.amount || 0), 0);
    const totalBusiness = data.filter(o => o.type === "business" || o.type === "Business").length;
    const totalPlatform = data.filter(o => o.type === "platform" || o.type === "Platform").length;
    const totalDriver = data.filter(o => o.type === "driver" || o.type === "Driver").length;
    
    return {
      orders: totalOrders,
      revenue: `￥${totalRevenue.toFixed(0)}`,
      business: totalBusiness,
      platform: totalPlatform,
      driver: totalDriver,
    };
  };

  // ───────────────────────────────────────────────────────────────────────────
  // DATE FILTER FUNCTIONS
  // ───────────────────────────────────────────────────────────────────────────
  const getDateRange = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = new Date(today);
    let end = new Date(today);

    switch (range) {
      case "today":
        start = new Date(today);
        end = new Date(today);
        break;
      case "yesterday":
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(today);
        end.setDate(end.getDate() - 1);
        break;
      case "last7":
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = new Date(today);
        break;
      case "last30":
        start = new Date(today);
        start.setDate(start.getDate() - 30);
        end = new Date(today);
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      default:
        start = new Date(today);
        end = new Date(today);
    }

    return { start, end };
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateFull = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const filterOrdersByDate = (orders, range) => {
    const { start, end } = getDateRange(range);
    
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return orders.filter(order => {
      const orderDate = new Date(order.created_at || order.date || order.orderDate);
      if (isNaN(orderDate.getTime())) return false;
      return orderDate >= start && orderDate <= end;
    });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // FETCH DATA
  // ───────────────────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("orders/reports");
      
      let ordersData = [];
      if (response && response.status === true && response.data) {
        ordersData = response.data;
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        ordersData = response.data;
      } else {
        ordersData = getMockOrders();
      }
      
      setOrders(ordersData);
      applyFilters(ordersData, dateRange, searchTerm);
    } catch (err) {
      console.error("Error fetching orders:", err);
      const mockData = getMockOrders();
      setOrders(mockData);
      applyFilters(mockData, dateRange, searchTerm);
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // MOCK DATA
  // ───────────────────────────────────────────────────────────────────────────
  const getMockOrders = () => {
    return [
      { id: 573, restaurant: "Madrasi Dosa", customer: "Neeraj Verma", amount: 234, status: "placed", payment: "COD", date: "2026-06-24 17:51:47", type: "platform" },
      { id: 572, restaurant: "Madrasi Dosa", customer: "Dhruv", amount: 286, status: "cancelled", payment: "COD", date: "2026-06-18 22:09:20", type: "business" },
      { id: 571, restaurant: "Royal Sweet Restaurant", customer: "Arjun Sharma", amount: 244, status: "cancelled", payment: "COD", date: "2026-06-16 13:54:00", type: "platform" },
      { id: 570, restaurant: "Neeraj Verma", customer: "Yash", amount: 441, status: "cancelled", payment: "COD", date: "2026-06-15 13:47:05", type: "driver" },
      { id: 569, restaurant: "Aditya Hotel", customer: "Neeraj Verma", amount: 327, status: "cancelled", payment: "COD", date: "2026-06-11 18:21:29", type: "platform" },
      { id: 568, restaurant: "Foodistaan", customer: "Yash", amount: 389, status: "cancelled", payment: "COD", date: "2026-06-06 22:11:46", type: "business" },
      { id: 567, restaurant: "Shiv Shankar Kulfi Faluda", customer: "Anup Singh Kushwah", amount: 191, status: "cancelled", payment: "COD", date: "2026-06-06 20:15:49", type: "platform" },
      { id: 566, restaurant: "Foodistaan", customer: "Anup Singh Kushwah", amount: 181, status: "cancelled", payment: "COD", date: "2026-06-06 20:15:49", type: "driver" },
      { id: 565, restaurant: "Karnataka cafe Jinsi road", customer: "shyam mourya", amount: 249, status: "cancelled", payment: "COD", date: "2026-06-05 16:56:11", type: "platform" },
      { id: 564, restaurant: "Shan E Punjab", customer: "shyam mourya", amount: 236, status: "cancelled", payment: "COD", date: "2026-06-05 16:54:56", type: "business" },
      { id: 563, restaurant: "Karnataka cafe Jinsi road", customer: "Yash", amount: 259, status: "placed", payment: "COD", date: "2026-06-03 17:23:27", type: "platform" },
      { id: 562, restaurant: "Punjab Grill", customer: "Rahul Sharma", amount: 520, status: "delivered", payment: "Online", date: "2026-06-02 19:30:00", type: "business" },
      { id: 561, restaurant: "Tandoori Nights", customer: "Priya Patel", amount: 380, status: "completed", payment: "COD", date: "2026-06-01 20:15:00", type: "driver" },
      { id: 560, restaurant: "Spice Garden", customer: "Amit Kumar", amount: 450, status: "confirmed", payment: "Online", date: "2026-05-31 18:45:00", type: "platform" },
      { id: 559, restaurant: "Biryani House", customer: "Sneha Reddy", amount: 310, status: "delivered", payment: "COD", date: "2026-05-30 21:00:00", type: "business" },
    ];
  };

  // ───────────────────────────────────────────────────────────────────────────
  // APPLY FILTERS
  // ───────────────────────────────────────────────────────────────────────────
  const applyFilters = (data, range, search) => {
    let filtered = [...data];

    if (range !== "custom") {
      filtered = filterOrdersByDate(filtered, range);
    }

    if (search && search.trim()) {
      const term = search.toLowerCase().trim();
      filtered = filtered.filter(order =>
        (order.id?.toString() || "").includes(term) ||
        (order.restaurant?.toLowerCase() || "").includes(term) ||
        (order.customer?.toLowerCase() || "").includes(term) ||
        (order.status?.toLowerCase() || "").includes(term)
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      applyFilters(orders, dateRange, searchTerm);
    }
  }, [dateRange, searchTerm, orders]);

  // ───────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────────
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setShowDropdown(false);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      const filtered = orders.filter(order => {
        const orderDate = new Date(order.created_at || order.date || order.orderDate);
        if (isNaN(orderDate.getTime())) return false;
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      });
      
      if (searchTerm && searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        setFilteredOrders(filtered.filter(order =>
          (order.id?.toString() || "").includes(term) ||
          (order.restaurant?.toLowerCase() || "").includes(term) ||
          (order.customer?.toLowerCase() || "").includes(term) ||
          (order.status?.toLowerCase() || "").includes(term)
        ));
      } else {
        setFilteredOrders(filtered);
      }
      setCurrentPage(1);
      setDateRange("custom");
      setShowDropdown(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const exportToCSV = () => {
    if (filteredOrders.length === 0) {
      alert("No data to export");
      return;
    }
    
    const headers = ['ID', 'Date', 'Restaurant', 'Customer', 'Amount', 'Status', 'Payment', 'Type'];
    const csvData = filteredOrders.map(o => [
      o.id || '',
      formatDateFull(o.date || o.created_at || o.orderDate) || '',
      o.restaurant || '',
      o.customer || '',
      `￥${o.amount || 0}`,
      o.status || '',
      o.payment || '',
      o.type || '—'
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // PAGINATION
  // ───────────────────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  // ───────────────────────────────────────────────────────────────────────────
  // STATS
  // ───────────────────────────────────────────────────────────────────────────
  const stats = calculateStats(filteredOrders);

  // ───────────────────────────────────────────────────────────────────────────
  // DATE RANGE DISPLAY
  // ───────────────────────────────────────────────────────────────────────────
  const getDateRangeDisplay = () => {
    if (dateRange === "custom") {
      return `${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
    const { start, end } = getDateRange(dateRange);
    return `${formatDate(start)} to ${formatDate(end)}`;
  };

  // ───────────────────────────────────────────────────────────────────────────
  // STYLES
  // ───────────────────────────────────────────────────────────────────────────
  const styles = {
    container: {
      minHeight: "100vh",
      background: isDark ? "#0d1117" : "#f9fafb",
      fontFamily: "'Segoe UI', 'DM Sans', sans-serif",
      padding: "20px 24px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      fontSize: "24px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#111827",
      margin: 0,
    },
    headerActions: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    exportBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 18px",
      background: "#22c55e",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    dateFilterWrapper: {
      position: "relative",
    },
    dateFilterBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "10px",
      fontSize: "13px",
      color: isDark ? "#f1f5f9" : "#111827",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    dateDropdown: {
      position: "absolute",
      top: "calc(100% + 8px)",
      right: 0,
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "8px",
      minWidth: "200px",
      zIndex: 50,
      boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    },
    dateOption: {
      display: "block",
      width: "100%",
      padding: "8px 14px",
      textAlign: "left",
      background: "none",
      border: "none",
      borderRadius: "6px",
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      cursor: "pointer",
      transition: "all 0.15s",
    },
    customDateSection: {
      padding: "12px",
      borderTop: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      marginTop: "8px",
    },
    customDateInput: {
      display: "block",
      width: "100%",
      padding: "6px 10px",
      marginBottom: "8px",
      background: isDark ? "#0d1117" : "#f9fafb",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "6px",
      fontSize: "12px",
      color: isDark ? "#f1f5f9" : "#111827",
      outline: "none",
    },
    customDateApply: {
      width: "100%",
      padding: "6px",
      background: "#3b82f6",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 600,
      cursor: "pointer",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "12px",
      marginBottom: "20px",
    },
    tableWrapper: {
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "12px",
      overflow: "hidden",
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "900px",
    },
    th: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "11px",
      fontWeight: 700,
      color: "#3b82f6",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      background: isDark ? "#0f1520" : "#f8fafc",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 16px",
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      borderBottom: isDark ? "1px solid #1a2035" : "1px solid #f1f5f9",
    },
    viewBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#64748b" : "#94a3b8",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    pagination: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 20px",
      borderTop: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      background: isDark ? "#0f1520" : "#f8fafc",
      flexWrap: "wrap",
      gap: "12px",
    },
    pageBtn: (disabled, isActive) => ({
      padding: "6px 12px",
      borderRadius: "6px",
      border: isActive ? "1px solid #3b82f6" : (isDark ? "1px solid #1e2740" : "1px solid #e5e7eb"),
      background: isActive ? "#1e3a8a" : (isDark ? "#141824" : "#ffffff"),
      color: isActive ? "#93c5fd" : (disabled ? (isDark ? "#2d3a55" : "#cbd5e1") : (isDark ? "#94a3b8" : "#64748b")),
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: "12px",
    }),
    modalOverlay: {
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modal: {
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
      borderRadius: "16px",
      width: "500px",
      maxWidth: "90vw",
      maxHeight: "80vh",
      overflow: "auto",
      padding: "28px",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#111827",
      marginBottom: "20px",
    },
    modalClose: {
      float: "right",
      background: "none",
      border: "none",
      fontSize: "20px",
      cursor: "pointer",
      color: isDark ? "#94a3b8" : "#6b7280",
    },
    modalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: isDark ? "1px solid #1a2035" : "1px solid #f1f5f9",
    },
    modalLabel: {
      fontSize: "13px",
      fontWeight: 500,
      color: isDark ? "#94a3b8" : "#6b7280",
    },
    modalValue: {
      fontSize: "13px",
      fontWeight: 600,
      color: isDark ? "#f1f5f9" : "#111827",
    },
    emptyState: {
      padding: "60px",
      textAlign: "center",
      color: isDark ? "#64748b" : "#94a3b8",
    },
    loadingState: {
      padding: "60px",
      textAlign: "center",
      color: isDark ? "#64748b" : "#94a3b8",
    },
  };

  // ───────────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { animation: spin 0.8s linear infinite; }
        button:hover { opacity: 0.9; }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Report</h1>
        <div style={styles.headerActions}>
          <button style={styles.exportBtn} onClick={exportToCSV}>
            <DownloadIcon /> Export CSV
          </button>
          
          {/* Date Filter Dropdown */}
          <div style={styles.dateFilterWrapper} ref={dropdownRef}>
            <button style={styles.dateFilterBtn} onClick={() => setShowDropdown(!showDropdown)}>
              <CalendarIcon />
              {dateRange === "custom" 
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : dateRange === "today" ? "Today"
                : dateRange === "yesterday" ? "Yesterday"
                : dateRange === "last7" ? "Last 7 Days"
                : dateRange === "last30" ? "Last 30 Days"
                : dateRange === "thisMonth" ? "This Month"
                : dateRange === "lastMonth" ? "Last Month"
                : "Select Date"}
              <ChevronDown />
            </button>
            
            {showDropdown && (
              <div style={styles.dateDropdown}>
                {["today", "yesterday", "last7", "last30", "thisMonth", "lastMonth"].map((range) => (
                  <button
                    key={range}
                    style={{
                      ...styles.dateOption,
                      background: dateRange === range ? (isDark ? "#1e2740" : "#f1f5f9") : "none",
                    }}
                    onClick={() => handleDateRangeChange(range)}
                  >
                    {range === "today" ? "Today"
                      : range === "yesterday" ? "Yesterday"
                      : range === "last7" ? "Last 7 Days"
                      : range === "last30" ? "Last 30 Days"
                      : range === "thisMonth" ? "This Month"
                      : "Last Month"}
                  </button>
                ))}
                <div style={styles.customDateSection}>
                  <input
                    type="date"
                    style={styles.customDateInput}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <input
                    type="date"
                    style={styles.customDateInput}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <button
                    style={styles.customDateApply}
                    onClick={handleCustomDateApply}
                    disabled={!startDate || !endDate}
                  >
                    Apply Custom Range
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <StatCard
          icon="📦"
          label="Orders"
          value={stats.orders}
          subValue={`Showing: ${getDateRangeDisplay()} (${stats.orders} orders)`}
          color="orders"
          isDark={isDark}
        />
        <StatCard
          icon="💰"
          label="Revenue"
          value={stats.revenue}
          subValue={`Avg: ￥${stats.orders > 0 ? (parseFloat(stats.revenue.replace('￥', '')) / stats.orders).toFixed(0) : 0}`}
          color="revenue"
          isDark={isDark}
        />
        <StatCard
          icon="🏢"
          label="Business"
          value={stats.business}
          color="business"
          isDark={isDark}
        />
        <StatCard
          icon="📱"
          label="Platform"
          value={stats.platform}
          color="platform"
          isDark={isDark}
        />
        <StatCard
          icon="🚗"
          label="Driver"
          value={stats.driver}
          color="driver"
          isDark={isDark}
        />
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          background: isDark ? "#141824" : "#ffffff",
          border: isDark ? "1px solid #1e2740" : "1px solid #e5e7eb",
          borderRadius: "10px",
          padding: "8px 14px",
          maxWidth: "350px",
        }}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              flex: 1,
              fontSize: "13px",
              color: isDark ? "#f1f5f9" : "#111827",
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isDark ? "#64748b" : "#94a3b8",
                fontSize: "14px",
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Restaurant</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Payment</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={styles.loadingState}>
                  <div className="spinner">⟳</div> Loading orders...
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan={9} style={styles.emptyState}>
                  No orders found for this period
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order) => (
                <tr
                  key={order.id}
                  style={{
                    ...styles.td,
                    background: isDark ? "transparent" : "transparent",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "#1a2035" : "#f8fafc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={styles.td}>#{order.id}</td>
                  <td style={styles.td}>
                    {formatDateFull(order.date || order.created_at || order.orderDate)}
                  </td>
                  <td style={styles.td}>{order.restaurant}</td>
                  <td style={styles.td}>{order.customer}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    ￥{order.amount}
                  </td>
                  <td style={styles.td}>
                    <StatusBadge status={order.status} isDark={isDark} />
                  </td>
                  <td style={styles.td}>
                    <PaymentBadge method={order.payment} isDark={isDark} />
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: 600,
                      background: order.type === "platform" || order.type === "Platform"
                        ? (isDark ? "rgba(236,72,153,0.15)" : "rgba(236,72,153,0.1)")
                        : order.type === "business" || order.type === "Business"
                        ? (isDark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)")
                        : (isDark ? "rgba(251,191,36,0.15)" : "rgba(251,191,36,0.1)"),
                      color: order.type === "platform" || order.type === "Platform"
                        ? "#ec4899"
                        : order.type === "business" || order.type === "Business"
                        ? "#8b5cf6"
                        : "#f59e0b",
                    }}>
                      {order.type || "—"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button style={styles.viewBtn} onClick={() => handleViewOrder(order)}>
                      <EyeIcon />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredOrders.length > itemsPerPage && (
          <div style={styles.pagination}>
            <span style={{ fontSize: "12px", color: isDark ? "#64748b" : "#94a3a8" }}>
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                style={styles.pageBtn(currentPage === 1, false)}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ←
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return pageNum <= totalPages && (
                  <button
                    key={pageNum}
                    style={styles.pageBtn(false, currentPage === pageNum)}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                style={styles.pageBtn(currentPage === totalPages, false)}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div style={styles.modalOverlay} onClick={() => setShowOrderModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setShowOrderModal(false)}>✕</button>
            <h2 style={styles.modalTitle}>Order Details #{selectedOrder.id}</h2>
            
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Date</span>
              <span style={styles.modalValue}>
                {formatDateFull(selectedOrder.date || selectedOrder.created_at || selectedOrder.orderDate)}
              </span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Restaurant</span>
              <span style={styles.modalValue}>{selectedOrder.restaurant}</span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Customer</span>
              <span style={styles.modalValue}>{selectedOrder.customer}</span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Amount</span>
              <span style={styles.modalValue}>￥{selectedOrder.amount}</span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Status</span>
              <span style={styles.modalValue}>
                <StatusBadge status={selectedOrder.status} isDark={isDark} />
              </span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Payment</span>
              <span style={styles.modalValue}>
                <PaymentBadge method={selectedOrder.payment} isDark={isDark} />
              </span>
            </div>
            <div style={styles.modalRow}>
              <span style={styles.modalLabel}>Type</span>
              <span style={styles.modalValue}>{selectedOrder.type || "—"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;